import {
  Checkbox,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Loader2 } from 'lucide-react';
import type { ProductGroupLevelCounts } from '@/lib/types';
import {
  MAX_PRODUCT_GROUP_LEVEL,
  type PotentialAnalysisProductGroupTypeSetting,
} from '@/lib/types/feature/potential-analysis-setting';
import { groupCountOf } from './estimate';

/** 1タイプぶんの選択状態 */
export interface TypeSelection {
  enabled: boolean;
  /** 出力する階層（1〜5）。最下層は 5 */
  productGroupLevel: number;
}

interface ProductGroupTypeTableProps {
  types: PotentialAnalysisProductGroupTypeSetting[];
  selections: Record<string, TypeSelection>;
  onChange: (typeId: string, next: Partial<TypeSelection>) => void;
  levelCounts: ProductGroupLevelCounts[] | null;
  levelCountsLoading: boolean;
}

/**
 * 出力する商品グループタイプの表（縦持ち。FKV3-13111）
 *
 * 1行 = 1タイプ。チェックが出力要否、セレクトが打ち切る階層を表す。
 * タイプごとのグループ数を出すのは、何も示さなければ最下層が選ばれ続けるため。
 */
export function ProductGroupTypeTable({
  types,
  selections,
  onChange,
  levelCounts,
  levelCountsLoading,
}: ProductGroupTypeTableProps) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">出力</TableCell>
            <TableCell>商品グループタイプ</TableCell>
            <TableCell sx={{ width: 160 }}>集計階層</TableCell>
            <TableCell align="right" sx={{ width: 160 }}>
              グループ数
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {types.map((type) => {
            const selection = selections[type.product_group_type_id];
            const counts = levelCounts?.find((c) => c.type_id === type.product_group_type_id);
            const groups = groupCountOf(
              counts,
              selection?.productGroupLevel ?? MAX_PRODUCT_GROUP_LEVEL,
            );
            return (
              <TableRow key={type.product_group_type_id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selection?.enabled ?? false}
                    onChange={(e) =>
                      onChange(type.product_group_type_id, { enabled: e.target.checked })
                    }
                    inputProps={{
                      'aria-label': `${type.name ?? type.product_group_type_id}を出力する`,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{type.name ?? type.product_group_type_id}</Typography>
                  {type.missing_from_master && (
                    <Typography variant="caption" className="text-amber-600">
                      マスタに存在しません
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    disabled={!selection?.enabled}
                    value={selection?.productGroupLevel ?? MAX_PRODUCT_GROUP_LEVEL}
                    onChange={(e) =>
                      onChange(type.product_group_type_id, {
                        productGroupLevel: Number(e.target.value),
                      })
                    }
                    inputProps={{
                      'aria-label': `${type.name ?? type.product_group_type_id}の集計階層`,
                    }}
                  >
                    {Array.from({ length: MAX_PRODUCT_GROUP_LEVEL }, (_, i) => i + 1).map(
                      (level) => (
                        <MenuItem key={level} value={level}>
                          {level === MAX_PRODUCT_GROUP_LEVEL
                            ? `${level}階層（最下層）`
                            : `${level}階層`}
                        </MenuItem>
                      ),
                    )}
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  {levelCountsLoading ? (
                    <Loader2 className="ml-auto h-3 w-3 animate-spin" />
                  ) : groups === null ? (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  ) : (
                    <Typography variant="body2">{groups.toLocaleString()} グループ</Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {types.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary">
                  商品グループタイプがありません
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
